/**
 * pptx-slide — pure model-builder for the PowerPoint slide renderer.
 *
 * Lifted verbatim out of `UniversalFileViewer` (the in-dialog `.pptx`
 * preview). Given one slide's raw `pptxtojson` AST, it walks the tree and
 * produces a flat, de-duplicated, priority-sorted, overlap-adjusted list of
 * positioned elements that the component then maps straight to absolutely-
 * positioned <Box>es. The heuristics (smart image/title/body positioning,
 * the skip-patterns, the z-index layering) are preserved exactly — this is a
 * behaviour-neutral extraction whose only point is to make ~240 lines of
 * dense, side-effect-free parsing logic unit-testable on its own.
 *
 * `content` / `style` / `position` stay `any` on purpose: the source AST is
 * untyped (`pptxtojson` emits arbitrary nested objects) and the consuming JSX
 * reads these loosely. Narrowing them here would be a behaviour change, not a
 * cleanup.
 */

export interface SlideElement {
  type: string
  content: any
  style: any
  position: any
  id: string
  priority?: number
}

/**
 * Walk one slide's raw AST (`pptxtojson` output) into the positioned,
 * filtered, sorted element list the renderer draws. Pure: no React, no I/O,
 * no shared state — only the closure-local accumulator.
 */
export function buildSlideElements(slideData: any): SlideElement[] {
  const slideElements: SlideElement[] = []
  let elementCounter = 0

  // Enhanced slide element parser
  const parseSlideElements = (obj: any, context: string = 'root') => {
    if (!obj) return

    // Handle base64 images with smart positioning
    if (typeof obj === 'string' && obj.startsWith('data:image')) {
      const existingImages = slideElements.filter(e => e.type === 'image').length

      // Smart image positioning based on content
      let position
      if (existingImages === 0) {
        // First image - could be background or main image
        position = {
          x: '0',
          y: '0',
          width: '100%',
          height: '100%',
          zIndex: 1 // Behind text
        }
      } else {
        // Additional images - position them as content
        position = {
          x: '15%',
          y: `${30 + (existingImages * 20)}%`,
          width: '70%',
          height: 'auto',
          zIndex: 3
        }
      }

      slideElements.push({
        type: 'image',
        content: obj,
        style: {
          objectFit: existingImages === 0 ? 'cover' : 'contain',
          borderRadius: existingImages === 0 ? '0' : '8px'
        },
        position: position,
        id: `img-${elementCounter++}`,
        priority: existingImages === 0 ? 0 : 4 // Background has highest priority
      })
      return
    }

    // Handle HTML-style text content with comprehensive parsing
    if (typeof obj === 'string' && (obj.includes('<') || obj.includes('style='))) {
      // Extract text and styling from HTML-like strings
      const cleanText = obj
        .replace(/&nbsp;/g, ' ')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&amp;/g, '&')
        .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags but keep content
        .trim()

      if (cleanText && cleanText.length > 2 && !cleanText.includes('data:image')) {
        // Extract styling information
        const fontSize = obj.match(/font-size:\s*(\d+)pt/)?.[1] ||
                       obj.match(/font-size:\s*(\d+)px/)?.[1] || '18'
        const fontFamily = obj.match(/font-family:\s*([^;"]+)/)?.[1]?.replace(/['"]/g, '') || 'Segoe UI, Arial, sans-serif'
        const textAlign = obj.match(/text-align:\s*(\w+)/)?.[1] || 'left'
        const color = obj.match(/color:\s*([^;"]+)/)?.[1] || '#000000'
        const fontWeight = obj.includes('font-weight: bold') || obj.includes('<strong>') ? 'bold' : 'normal'

        // Smart text positioning and layout
        const textElements = slideElements.filter(e => e.type === 'text')
        const imageElements = slideElements.filter(e => e.type === 'image')
        const isLargeText = parseInt(fontSize) > 30
        const isMediumText = parseInt(fontSize) > 20
        const isTitle = isLargeText || (textElements.length === 0 && cleanText.length < 50)

        let position

        if (isTitle) {
          // Title positioning - top center
          position = {
            x: '5%',
            y: '15%',
            width: '90%',
            height: 'auto',
            zIndex: 10
          }
        } else {
          // Body text positioning - avoid overlaps
          const hasImages = imageElements.length > 0
          const titleExists = textElements.some(e => parseInt(e.style.fontSize) > 24)
          const startY = titleExists ? 35 : 20
          const textIndex = textElements.length

          if (hasImages && textElements.length < 3) {
            // Position text next to or below images
            position = {
              x: '10%',
              y: `${startY + (textIndex * 12)}%`,
              width: '80%',
              height: 'auto',
              zIndex: 5
            }
          } else {
            // Regular text flow
            position = {
              x: '10%',
              y: `${startY + (textIndex * 10)}%`,
              width: '80%',
              height: 'auto',
              zIndex: 5
            }
          }
        }

        slideElements.push({
          type: 'text',
          content: cleanText,
          style: {
            fontSize: `${Math.max(parseInt(fontSize), 14)}px`,
            fontFamily: fontFamily,
            textAlign: textAlign,
            color: color,
            fontWeight: isTitle ? 'bold' : fontWeight,
            lineHeight: '1.3',
            wordWrap: 'break-word',
            display: 'block',
            marginBottom: '8px',
            textShadow: isTitle ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'
          },
          position: position,
          id: `text-${elementCounter++}`,
          priority: isTitle ? 1 : isMediumText ? 2 : 3
        })
      }
    }

    // Handle pure text strings with better positioning
    if (typeof obj === 'string' && obj.trim() &&
        !obj.includes('<') && !obj.includes('data:') &&
        obj.length > 2 && !obj.includes('style=')) {
      const textElements = slideElements.filter(e => e.type === 'text')
      const cleanText = obj.trim()

      // Skip common non-content text
      const skipPatterns = ['image', 'false', 'null', 'undefined', 'true', '0', '1', '2']
      if (skipPatterns.includes(cleanText.toLowerCase()) || /^\d+$/.test(cleanText)) {
        return
      }

      const isFirstText = textElements.length === 0
      const textIndex = textElements.length

      slideElements.push({
        type: 'text',
        content: cleanText,
        style: {
          fontSize: isFirstText && cleanText.length < 30 ? '24px' : '16px',
          fontFamily: 'Segoe UI, Arial, sans-serif',
          textAlign: 'left',
          color: '#333333',
          fontWeight: isFirstText ? 'bold' : 'normal',
          lineHeight: '1.4',
          marginBottom: '6px'
        },
        position: {
          x: '10%',
          y: isFirstText ? '15%' : `${35 + (textIndex * 8)}%`,
          width: '80%',
          height: 'auto',
          zIndex: isFirstText ? 10 : 5
        },
        id: `text-${elementCounter++}`,
        priority: isFirstText ? 1 : 3
      })
    }

    // Recursively parse nested structures
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => parseSlideElements(item, `${context}[${index}]`))
    } else if (typeof obj === 'object') {
      Object.keys(obj).forEach(key => {
        if (obj[key] !== null && obj[key] !== undefined) {
          parseSlideElements(obj[key], `${context}.${key}`)
        }
      })
    }
  }

  // Start parsing the slide data
  parseSlideElements(slideData)

  // Advanced element filtering and optimization
  const filteredElements = slideElements
    // Remove duplicates by content
    .filter((element, index, self) =>
      index === self.findIndex(e => e.content === element.content && e.type === element.type)
    )
    // Remove very short or meaningless text
    .filter(element => {
      if (element.type === 'text') {
        const content = element.content.trim().toLowerCase()
        // Remove single characters, numbers, or common meaningless words
        return content.length > 1 &&
               !['up', 'down', 'left', 'right', 'color', 'solid'].includes(content) &&
               !/^[\d\s]*$/.test(content)
      }
      return true
    })
    // Sort by priority (titles first, then by position)
    .sort((a, b) => {
      const aPriority = a.priority || 999
      const bPriority = b.priority || 999
      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }
      // Sort by Y position
      const aY = parseFloat(a.position.y) || 0
      const bY = parseFloat(b.position.y) || 0
      return aY - bY
    })

  // Optimize positioning to prevent overlaps
  let currentY = 15 // Start after header area
  filteredElements.forEach((element, index) => {
    if (element.type === 'text') {
      const isTitle = (element.priority || 999) === 1
      if (!isTitle && index > 0) {
        // Adjust Y position to prevent overlap
        currentY += 8
        element.position.y = `${currentY}%`
      } else if (isTitle) {
        element.position.y = '8%'
        currentY = 25 // Reset for body text
      }
    }
  })

  return filteredElements
}
